// Firebase provider — Firestore-mediated WebRTC signaling.
//
// Relocated (S2) from src/lib/p2p/signaling.js, verbatim except: the lazy SDK
// handle now resolves via `getDb()` (the original grabbed `mod.db`, which S3
// removes), and the relative import paths are rebased to the new location:
//   ./limits.js          -> ../../p2p/limits.js
//   ../firebase-health.js -> ../../firebase-health.js
//   ../firebase.js        -> ../../firebase.js
//
// Schema (Firestore):
//   parties/{partyCode}/signals/{signalId} = {
//     from: string,      sender peerId (== identity.user)
//     to:   string,      recipient peerId
//     type: 'offer' | 'answer' | 'ice',
//     payload: object,   SDP or ICE candidate
//     createdAt: serverTimestamp
//   }
//
// Lifecycle: caller writes; recipient subscribes filtered by `to == myPeerId`;
// recipient calls `deleteSignal()` after consuming the payload. This keeps
// the signals subcollection bounded — entries don't linger past handshake.
//
// Rate-limiting (F14.3): a single client cannot write more than 20 docs per
// 10 s window. Excess writes queue locally; the queue overflows above
// SIGNAL_QUEUE_OVERFLOW and throws.

import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
} from '@firebase/firestore';
import { setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';

import { LIMITS } from '../../p2p/limits.js';
import { reportFirestoreError } from '../../firebase-health.js';

// We lazy-import the SDK handle so callers can stub `../../firebase` in tests
// without `signaling.js` failing at module load if env vars are missing. The
// underlying `getDb()` is itself memoized and only validates env when called.
// Fall back to `mod.db` when a test mock exposes only `db` (not `getDb`); real
// code uses the lazy memoized `getDb`. See docs/features/supabase/.
let _db = null;
async function resolveDb() {
  if (_db) return _db;
  const mod = await import('../../firebase.js');
  // Access defensively: a Vitest mock namespace THROWS on reading an export it
  // didn't define, so a test exposing only `db` would blow up on `mod.getDb`.
  let getter;
  try {
    getter = mod.getDb;
  } catch {
    getter = undefined;
  }
  _db = typeof getter === 'function' ? getter() : mod.db;
  return _db;
}

// ── Token-bucket rate limiter ───────────────────────────────────────────────
//
// Keyed by partyCode: each room has its own bucket. Tokens replenish at the
// SIGNAL_TOKENS_PER_INTERVAL / SIGNAL_INTERVAL_MS rate. When a write would
// exhaust the bucket, it enters a per-code FIFO queue that's drained as
// tokens free up.

/** @type {Map<string, {tokens: number, queue: Array<{resolve:Function,reject:Function,write:Function}>, lastRefill: number, draining: boolean}>} */
const buckets = new Map();

function getBucket(code) {
  let b = buckets.get(code);
  if (!b) {
    b = {
      tokens: LIMITS.SIGNAL_TOKENS_PER_INTERVAL,
      queue: [],
      lastRefill: Date.now(),
      draining: false,
    };
    buckets.set(code, b);
  }
  return b;
}

function refill(bucket) {
  const now = Date.now();
  const elapsed = now - bucket.lastRefill;
  if (elapsed <= 0) return;
  const newTokens = (elapsed / LIMITS.SIGNAL_INTERVAL_MS) * LIMITS.SIGNAL_TOKENS_PER_INTERVAL;
  bucket.tokens = Math.min(
    LIMITS.SIGNAL_TOKENS_PER_INTERVAL,
    bucket.tokens + newTokens,
  );
  bucket.lastRefill = now;
}

function scheduleDrain(code) {
  const bucket = getBucket(code);
  if (bucket.draining) return;
  if (bucket.queue.length === 0) return;
  bucket.draining = true;

  const tick = () => {
    refill(bucket);
    while (bucket.tokens >= 1 && bucket.queue.length > 0) {
      bucket.tokens -= 1;
      const job = bucket.queue.shift();
      // Fire-and-forget — the original caller's promise is hooked up to
      // job.resolve/reject.
      Promise.resolve()
        .then(job.write)
        .then(job.resolve, job.reject);
    }
    if (bucket.queue.length === 0) {
      bucket.draining = false;
      return;
    }
    // Schedule next drain at the per-token interval.
    const msPerToken = LIMITS.SIGNAL_INTERVAL_MS / LIMITS.SIGNAL_TOKENS_PER_INTERVAL;
    setTimeout(tick, Math.max(50, msPerToken));
  };

  // First tick can fire immediately — we may have just refilled.
  setTimeout(tick, 0);
}

/**
 * Send a signaling message. Subject to rate-limiting (queues over the limit,
 * throws on overflow).
 *
 * @param {string} code  partyCode
 * @param {{from:string, to:string, type:'offer'|'answer'|'ice', payload:object}} sig
 * @returns {Promise<void>}
 */
export async function sendSignal(code, sig) {
  if (!code) throw new TypeError('sendSignal: code is required');
  if (!sig || !sig.from || !sig.to || !sig.type) {
    throw new TypeError('sendSignal: from/to/type required');
  }
  const bucket = getBucket(code);
  refill(bucket);

  const write = async () => {
    const db = await resolveDb();
    if (!db) return; // tests may stub db as null/undefined; treat as no-op
    const signalsCol = collection(db, 'parties', code, 'signals');
    const ref = doc(signalsCol);
    try {
      await setDoc(ref, {
        from: String(sig.from),
        to: String(sig.to),
        type: String(sig.type),
        payload: sig.payload ?? {},
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      // Surface so the user sees why the WebRTC handshake stalled. The
      // MeshProvider's SIGNAL_OVERFLOW catch handles client-side rate
      // limits; this catch covers server-side failures (quota, perms).
      reportFirestoreError(err);
      throw err;
    }
  };

  if (bucket.tokens >= 1 && bucket.queue.length === 0) {
    bucket.tokens -= 1;
    return write();
  }

  if (bucket.queue.length >= LIMITS.SIGNAL_QUEUE_OVERFLOW) {
    const err = new Error('Signaling queue overflow');
    err.code = 'SIGNAL_OVERFLOW';
    throw err;
  }

  return new Promise((resolve, reject) => {
    bucket.queue.push({ resolve, reject, write });
    scheduleDrain(code);
  });
}

/**
 * Subscribe to incoming signals for `myPeerId`. The caller is responsible
 * for calling `deleteSignal(code, id)` after consuming each one.
 *
 * @param {string} code
 * @param {string} myPeerId
 * @param {(sig: {id:string, from:string, to:string, type:string, payload:object}) => void} onSignal
 * @returns {() => void}  unsubscribe
 */
export function subscribeIncoming(code, myPeerId, onSignal) {
  if (!code || !myPeerId) return () => {};
  let unsubbed = false;
  let unsub = () => {
    unsubbed = true;
  };

  (async () => {
    const db = await resolveDb();
    if (!db || unsubbed) return;
    const signalsCol = collection(db, 'parties', code, 'signals');
    const q = query(signalsCol, where('to', '==', myPeerId));
    const stop = onSnapshot(
      q,
      (snapshot) => {
        snapshot.forEach((d) => {
          const data = d.data() || {};
          onSignal({
            id: d.id,
            from: data.from,
            to: data.to,
            type: data.type,
            payload: data.payload || {},
          });
        });
      },
      (err) => reportFirestoreError(err),
    );
    if (unsubbed) {
      stop();
    } else {
      unsub = stop;
    }
  })();

  return () => {
    unsubbed = true;
    try {
      unsub();
    } catch {
      /* ignore */
    }
  };
}

/**
 * Delete a consumed signal doc. Best-effort.
 *
 * @param {string} code
 * @param {string} signalId
 */
export async function deleteSignal(code, signalId) {
  if (!code || !signalId) return;
  try {
    const db = await resolveDb();
    if (!db) return;
    await deleteDoc(doc(db, 'parties', code, 'signals', signalId));
  } catch (err) {
    reportFirestoreError(err);
  }
}

/**
 * Test-only: reset the rate-limit buckets. Real callers never need this.
 */
export function __resetBuckets() {
  buckets.clear();
}

export const __test__ = { buckets, getBucket, refill };
